import {
    ClippingMode,
    DeviceProfile,
    type EnvironmentDescription,
    type PointVisualization,
    type RecursivePartial,
    type SceneConfig as OctreeSceneConfig,
    TonemappingMode,
    View,
} from "@novorender/api";
import type { Bookmark } from "@novorender/data-js-api";
import { createAction, createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { quat, vec3, vec4 } from "gl-matrix";

import { PipelineConfig, ProjectInfo } from "apis/dataV2/projectTypes";
import type { RootState } from "app";
import type { SetViewpointPayload } from "features/viewpoints/types";
import type { ProjectType } from "slices/explorer";
import { UxFlavor } from "slices/explorer/types";
import { AsyncState, AsyncStatus, Color, InternalPaletteParams, ViewMode } from "types/misc";
import { fullFormHexToVecRGBA, type VecRGB, type VecRGBA } from "utils/color";
import { mergeRecursive } from "utils/misc";

import {
    type CadCamera,
    CameraSpeedLevel,
    type CameraState,
    type CameraStep,
    CameraType,
    type ClippingPlane,
    LabeledKnot,
    ObjectVisibility,
    Picker,
    type SavedCameraPositions,
    type SceneConfig,
    SelectionBasketMode,
    type Stamp,
    type Subtree,
    type Subtrees,
    SubtreeStatus,
} from "./types";
import { DEFAULT_CAMERA_PINHOLE_FAR, getLegacySubtrees, getSubtrees, viewpointCameraToCadCamera } from "./utils";

const initialState = {
    sceneStatus: { status: AsyncStatus.Initial } as AsyncState<void>,
    sceneOrganization: "",
    mainObject: undefined as number | undefined,
    markerDragHoverObject: undefined as number | undefined,
    defaultVisibility: ObjectVisibility.Neutral,
    preIsolationVisibility: undefined as ObjectVisibility | undefined,
    useFilterForTransparency: false,
    selectMultiple: false,
    selectBox: null as null | { startX: number; endX: number; startY: number; endY: number },
    currentCameraSpeedLevel: CameraSpeedLevel.Default,
    savedCameraPositions: { currentIndex: -1, positions: [] as CameraStep[] },
    subtrees: {
        triangles: SubtreeStatus.Unavailable,
        lines: SubtreeStatus.Unavailable,
        terrain: SubtreeStatus.Unavailable,
        points: SubtreeStatus.Unavailable,
        documents: SubtreeStatus.Unavailable,
    } as Subtrees,
    selectionBasketMode: SelectionBasketMode.Loose,
    selectionBasketColor: {
        color: [0, 0, 1, 1] as VecRGB | VecRGBA,
        use: false,
    },
    camera: { type: CameraType.Pinhole } as CameraState,
    initialCamera: undefined as CadCamera | undefined,
    recordCameraMoveUndoRedo: true,
    picker: Picker.Object,
    viewMode: ViewMode.Default,
    isSubtreesPanelOpen: false,
    loadingHandles: [] as number[],
    stamp: null as null | Stamp,
    background: {
        environments: { status: AsyncStatus.Initial } as AsyncState<EnvironmentDescription[]>,
        color: [0.75, 0.75, 0.75, 1] as VecRGBA,
        url: "",
        blur: 1,
        alpha: 1,
    },
    xrayConfig: {
        color: [0.73, 0.73, 0.73, 0.2] as VecRGBA,
    },
    clipping: {
        outlines: true,
        enabled: false,
        draw: false,
        mode: ClippingMode.union,
        planes: [] as ClippingPlane[],
    },
    grid: {
        enabled: false,
        distance: 500,
        origin: [0, 0, 0] as vec3,
        axisX: [0, 0, 0] as vec3,
        axisY: [0, 0, 0] as vec3,
        color1: [0.65, 0.65, 0.65] as vec3,
        color2: [0.15, 0.15, 0.15] as vec3,
        size1: 1,
        size2: 5,
    },
    points: {
        size: {
            pixel: 1,
            maxPixel: 20,
            metric: 1,
            toleranceFactor: 0.4,
        },
        deviation: {
            index: 0,
            mixFactor: 1,
            colorGradient: {
                knots: [] as { position: number; color: VecRGBA }[],
            },
        },
        classificationColorGradient: {
            knots: [] as LabeledKnot[],
            undefinedColor: [0, 0, 0, 1] as VecRGBA,
            highlightedPosition: undefined as number | undefined,
        },
        defaultPointVisualization: { kind: "color" } as PointVisualization,
    },
    terrain: {
        asBackground: false,
        doubleSided: false,
        elevationGradient: {
            knots: [] as { position: number; color: VecRGB }[],
        },
        canSelect: false,
    },
    secondaryHighlight: {
        property: "",
    },
    project: {
        tmZone: "",
    },
    cameraDefaults: {
        pinhole: {
            controller: "flight" as "flight" | "cadMiddlePan" | "cadRightPan" | "special" | "hover",
            clipping: {
                near: 0.1,
                far: DEFAULT_CAMERA_PINHOLE_FAR,
            },
            speedLevels: {
                slow: 0.3,
                default: 1,
                fast: 5,
            },
            proportionalSpeed: {
                enabled: true,
                min: 0.1,
                max: 300,
            },
            usePointerLock: true,
        },
        orthographic: {
            controller: "ortho" as const,
            clipping: {
                near: -0.1,
                far: 1000,
            },
            usePointerLock: false,
            topDownElevation: undefined as undefined | number,
            topDownSnapToAxis: undefined as undefined | "north",
            deAcceleration: false,
            touchRotate: false,
        },
    },
    cameraDefaultFlyTime: undefined as number | undefined,
    advanced: {
        dynamicResolutionScaling: true,
        msaa: {
            enabled: true,
            samples: 16,
        },
        toonOutline: {
            enabled: true,
            color: [0, 0, 0] as VecRGB,
            onlyOnIdleFrame: true,
            outlineObjects: false,
        },
        outlines: {
            enabled: false,
            color: [10, 10, 10] as VecRGB,
            plane: [0, -1, 0, 0] as vec4,
            breakingPointAngleThreshold: 30 as number,
        },
        tonemapping: {
            exposure: 0.5,
            mode: TonemappingMode.color,
        },
        pick: {
            opacityThreshold: 1,
            xrayPickingEnabled: false,
        },
        limits: {
            maxPrimitives: 5_000_000,
        },
        // NOTE(OLA): Debug props should not be saved
        debug: {
            showNodeBounds: false,
        },
    },
    deviceProfile: {
        debugProfile: false,
        isMobile: false,
        tier: 0 as DeviceProfile["tier"],
        features: {
            outline: true,
        },
        limits: {
            maxGPUBytes: 2_000_000_000,
            maxPrimitives: 10_000_000,
            maxSamples: 4,
        },
        quirks: {
            adreno600: false,
            slowShaderRecompile: false,
        },
        detailBias: 0.6,
        renderResolution: 1,
        framerateTarget: 30,
        autofreezeEnabled: true,
    },
    generatedParametricData: {
        enabled: true,
    },
    navigationCube: {
        enabled: false,
        showCompass: true,
    },
    debugStats: {
        enabled: false,
    },
    clippingInEdit: false,
    colorOverrides: {
        object2normalColor: {} as Record<number, number[]>,
        normalColor2objects: {} as Record<string, number[]>,
        object2xrayColor: {} as Record<number, number[]>,
        xrayColor2objects: {} as Record<string, number[]>,
        object2selectedColor: {} as Record<number, number[]>,
        selectedColor2objects: {} as Record<string, number[]>,
    },
    canvasSelectionEnabled: true,
    section: {
        planeHoverObjectId: null as number | null,
    },
};

type State = typeof initialState;

/**
 * @internal
 * Sets the opacity threshold slightly above the xRayOpacity to ensure proper rendering.
 * This offset prevents the selection of "ghost" objects when in XRay mode.
 * @type number
 * @default 0.001
 */
const XRAY_OPACITY_THRESHOLD_OFFSET: number = 0.001;

/**
 * @internal
 * This is used to set the opacity threshold for picking objects in the scene.
 * The value is a number between 0 and 1,
 * where 0 is fully transparent and 1 is fully opaque.
 * This threshold is used to determine whether an object is considered opaque enough
 * to be picked by the user.
 * It is particularly useful in scenarios where you want to ensure that
 * objects with very low opacity are not picked,
 * as they may be visually indistinguishable from the background or other objects.
 * @type number
 * @default 0.1
 */
const NORMAL_VIZ_MODE_OPACITY_THRESHOLD: number = 0.1;

export const initScene = createAction<{
    projectType: ProjectType;
    projectV2Info: ProjectInfo;
    pipelineConfig: PipelineConfig | undefined;
    tmZoneForCalc: string | undefined;
    sceneData: Omit<SceneConfig, "db" | "url">;
    sceneConfig: OctreeSceneConfig;
    initialCamera: CadCamera;
    isVersionComparison: boolean;
    deviceProfile: RecursivePartial<State["deviceProfile"]>;
    assetsUrl: string;
    uxFlavor: UxFlavor;
}>("initScene");

export const resetView = createAction<{
    sceneData: Omit<SceneConfig, "db" | "url" | "settings"> & { settings?: SceneConfig["settings"] };
    initialCamera?: CadCamera;
}>("resetView");

export const selectBookmark = createAction(
    "selectBookmark",
    (payload: NonNullable<Bookmark["explorerState"]> & { autofreezeEnabled?: boolean }, view: View) => ({
        payload,
        meta: { view },
    }),
);

export const setViewpoint = createAction(
    "setViewpoint",
    (
        payload: SetViewpointPayload,
        view: View,
        publicIdsToObjectIds: Map<string, number>,
        options?: { ignoreUndefined?: boolean },
    ) => ({
        payload,
        meta: { view, publicIdsToObjectIds, ignoreUndefined: options?.ignoreUndefined ?? false },
    }),
);

/**
 * Automatically clears `preIsolationVisibility` when visibility transitions to a non-isolated mode
 * (Neutral or SemiTransparent). This prevents stale isolation state from causing "Unisolate" to
 * appear incorrectly after code paths that leave isolation without explicitly clearing it.
 */
function clearPreIsolationIfNotIsolated(state: State): void {
    if (state.defaultVisibility !== ObjectVisibility.Transparent && state.defaultVisibility !== ObjectVisibility.Xray) {
        state.preIsolationVisibility = undefined;
    }
}

export const renderSlice = createSlice({
    name: "render",
    initialState: initialState as State,
    reducers: {
        setMainObject: (state, action: PayloadAction<number | undefined>) => {
            state.mainObject = action.payload;
        },
        setMarkerDragHoverObject: (state, action: PayloadAction<number | undefined>) => {
            state.markerDragHoverObject = action.payload;
        },
        unsetMainObjectIfMatches: (state, action: PayloadAction<number>) => {
            if (state.mainObject === action.payload) {
                state.mainObject = undefined;
            }
        },
        toggleDefaultVisibility: (state) => {
            switch (state.defaultVisibility) {
                case ObjectVisibility.Neutral:
                    state.defaultVisibility = ObjectVisibility.SemiTransparent;
                    break;
                case ObjectVisibility.SemiTransparent:
                    state.defaultVisibility = ObjectVisibility.Transparent;
                    break;
                case ObjectVisibility.Xray:
                    state.defaultVisibility = ObjectVisibility.Neutral;
                    break;
                case ObjectVisibility.Transparent:
                    state.defaultVisibility = ObjectVisibility.Neutral;
            }
            clearPreIsolationIfNotIsolated(state);
        },
        setDefaultVisibility: (state, action: PayloadAction<ObjectVisibility>) => {
            state.defaultVisibility = action.payload;
            clearPreIsolationIfNotIsolated(state);
        },
        /**
         * Stores the visibility mode to restore to when leaving an isolated visibility state,
         * regardless of how that isolated state was entered (user isolation, bookmarks/viewpoints, etc.).
         * Used to restore the correct mode when unisolating, and to distinguish isolation-related Transparent
         * mode from other features that also set Transparent mode (e.g. BimTrack, BimCollab, Deviations).
         */
        setPreIsolationVisibility: (state, action: PayloadAction<ObjectVisibility>) => {
            state.preIsolationVisibility = action.payload;
        },
        clearPreIsolationVisibility: (state) => {
            state.preIsolationVisibility = undefined;
        },
        setUseFilterForTransparency: (state, action: PayloadAction<boolean>) => {
            state.useFilterForTransparency = action.payload;
        },
        /**
         * !!! README !!!
         * The `setVisibilityMode()` function was introduced specifically to leave `setDefaultVisibility()` unchanged.
         * The `setVisibilityMode()` function supports the switching of the visual mode between "X-Ray" and "Normal", in
         * the context of the webviewer re-skinning effort and the porting of Procore SDK. Since the mode switching
         * involves fiddling with the opacity threshold, putting this logic in `setDefaultVisibility()` could have
         * introduced unintended and unexpected behavior when the viewer is running in "non-Procore" mode, in particular
         * when setting the default visibility to "Neutral". Since the Procore experience uses a different opacity
         * threshold for picking than the base implementation, there was a risk of altering the picking behavior for the
         * non-Procore consumers of the viewer.
         * ...To make a long story short, The`setVisibilityMode()` function can be seen as a form of risk mitigation.
         */
        setVisibilityMode: (state, action: PayloadAction<ObjectVisibility>) => {
            state.defaultVisibility = action.payload;
            clearPreIsolationIfNotIsolated(state);
            switch (state.defaultVisibility) {
                case ObjectVisibility.Xray:
                    state.advanced = mergeRecursive(state.advanced, {
                        pick: {
                            opacityThreshold: state.advanced.pick.xrayPickingEnabled
                                ? NORMAL_VIZ_MODE_OPACITY_THRESHOLD
                                : state.xrayConfig.color[3] + XRAY_OPACITY_THRESHOLD_OFFSET,
                        },
                    });
                    break;
                case ObjectVisibility.Neutral:
                    state.advanced = mergeRecursive(state.advanced, {
                        pick: { opacityThreshold: NORMAL_VIZ_MODE_OPACITY_THRESHOLD },
                    });
                    break;
            }
        },
        toggleXRayMode: (state) => {
            if (state.defaultVisibility === ObjectVisibility.Xray) {
                state.defaultVisibility = ObjectVisibility.Neutral;
                state.preIsolationVisibility = undefined;
                state.advanced = mergeRecursive(state.advanced, {
                    pick: { opacityThreshold: NORMAL_VIZ_MODE_OPACITY_THRESHOLD },
                });
            } else {
                state.defaultVisibility = ObjectVisibility.Xray;
                state.advanced = mergeRecursive(state.advanced, {
                    pick: {
                        opacityThreshold: state.advanced.pick.xrayPickingEnabled
                            ? NORMAL_VIZ_MODE_OPACITY_THRESHOLD
                            : state.xrayConfig.color[3] + XRAY_OPACITY_THRESHOLD_OFFSET,
                    },
                });
            }
        },
        toggleSelectMultiple: (state) => {
            state.selectMultiple = !state.selectMultiple;
        },
        setSelectMultiple: (state, action: PayloadAction<boolean>) => {
            state.selectMultiple = action.payload;
        },
        setSelectBox: (state, action: PayloadAction<State["selectBox"]>) => {
            state.selectBox = action.payload;
        },
        toggleCameraSpeed: (state) => {
            switch (state.currentCameraSpeedLevel) {
                case CameraSpeedLevel.Slow:
                    state.currentCameraSpeedLevel = CameraSpeedLevel.Default;
                    break;
                case CameraSpeedLevel.Default:
                    state.currentCameraSpeedLevel = CameraSpeedLevel.Fast;
                    break;
                case CameraSpeedLevel.Fast:
                    state.currentCameraSpeedLevel = CameraSpeedLevel.Slow;
                    break;
            }
        },
        /**
         * Save camera position at current index.
         *
         * @remarks
         * Deletes camera positions already saved at higher indexes
         */
        saveCameraPosition: (state, action: PayloadAction<CameraStep>) => {
            const lastPos = state.savedCameraPositions.positions[state.savedCameraPositions.currentIndex];

            if (
                lastPos &&
                vec3.equals(action.payload.position, lastPos.position) &&
                quat.equals(action.payload.rotation, lastPos.rotation) &&
                lastPos.fov &&
                lastPos.fov === action.payload.fov &&
                action.payload.kind === lastPos.kind
            ) {
                return state;
            }

            state.savedCameraPositions.positions = state.savedCameraPositions.positions
                .slice(0, state.savedCameraPositions.currentIndex + 1)
                .concat({
                    ...action.payload,
                    position: vec3.clone(action.payload.position),
                    rotation: quat.clone(action.payload.rotation),
                });
            state.savedCameraPositions.currentIndex = state.savedCameraPositions.positions.length - 1;
        },
        undoCameraPosition: (state) => {
            state.savedCameraPositions.currentIndex = state.savedCameraPositions.currentIndex - 1;
            const step = state.savedCameraPositions.positions[state.savedCameraPositions.currentIndex];
            state.camera = {
                type: step.kind,
                goTo: step,
            };
        },
        redoCameraPosition: (state) => {
            state.savedCameraPositions.currentIndex = state.savedCameraPositions.currentIndex + 1;
            const step = state.savedCameraPositions.positions[state.savedCameraPositions.currentIndex];
            state.camera = {
                type: step.kind,
                goTo: step,
            };
        },
        setHomeCameraPos: (state, action: PayloadAction<CameraStep>) => {
            state.savedCameraPositions.positions[0] = {
                ...action.payload,
                position: vec3.clone(action.payload.position),
                rotation: quat.clone(action.payload.rotation),
            };
        },
        setSubtrees: (state, action: PayloadAction<State["subtrees"]>) => {
            state.subtrees = action.payload;
        },
        toggleSubtree: (state, action: PayloadAction<{ subtree: Subtree; newState?: SubtreeStatus }>) => {
            if (!state.subtrees || state.subtrees[action.payload.subtree] === SubtreeStatus.Unavailable) {
                return;
            }

            state.subtrees[action.payload.subtree] =
                action.payload.newState ??
                (state.subtrees[action.payload.subtree] === SubtreeStatus.Shown
                    ? SubtreeStatus.Hidden
                    : SubtreeStatus.Shown);
        },
        setSubtreesVisible: (state, action: PayloadAction<Partial<Record<Subtree, boolean>>>) => {
            for (const [subtree, visible] of Object.entries(action.payload) as [Subtree, boolean][]) {
                const currentState = state.subtrees[subtree];
                const newState = visible ? SubtreeStatus.Shown : SubtreeStatus.Hidden;
                if (currentState && currentState !== SubtreeStatus.Unavailable && currentState !== newState) {
                    state.subtrees[subtree] = newState;
                }
            }
        },
        setSubtreesFromBookmark: (state, action: PayloadAction<Required<Bookmark>["subtrees"]>) => {
            state.subtrees = subtreesFromBookmark(state.subtrees, action.payload);
        },
        setSelectionBasketMode: (state, action: PayloadAction<State["selectionBasketMode"]>) => {
            state.selectionBasketMode = action.payload;
        },
        setSelectionBasketColor: (state, action: PayloadAction<Partial<State["selectionBasketColor"]>>) => {
            state.selectionBasketColor = { ...state.selectionBasketColor, ...action.payload };
        },
        setClippingPlanes: (state, action: PayloadAction<RecursivePartial<State["clipping"]>>) => {
            state.clipping = mergeRecursive(state.clipping, action.payload);
            state.clipping.planes = state.clipping.planes.map((plane, idx) => ({
                ...plane,
                color: clippingPlaneColors[idx],
                outline: {
                    enabled: plane.outline ? plane.outline.enabled : idx === 0,
                    color: clippingPlaneOutlineColors[idx],
                },
            }));
        },
        toggleClippingPlaneOutlines: (state, action: PayloadAction<number>) => {
            const plane = state.clipping.planes[action.payload];
            if (plane.outline) {
                plane.outline.enabled = !plane.outline.enabled;
            }
        },
        addClippingPlane: (state, action: PayloadAction<Omit<ClippingPlane, "color" | "outline">>) => {
            if (vec3.exactEquals(action.payload.normalOffset.slice(0, 3) as Vec3, [0, 0, 0])) {
                return state;
            }

            state.clipping.enabled = true;

            if (state.clipping.planes.length < 6) {
                state.clipping.planes.push({
                    ...action.payload,
                    color: clippingPlaneColors[state.clipping.planes.length],
                    outline: {
                        enabled: !state.clipping.planes.length,
                        color: clippingPlaneOutlineColors[state.clipping.planes.length],
                    },
                });
            }
        },
        flipClippingPlane: (state, action: PayloadAction<number>) => {
            const plane = state.clipping.planes[action.payload];
            if (plane) {
                plane.normalOffset = [
                    -plane.normalOffset[0],
                    -plane.normalOffset[1],
                    -plane.normalOffset[2],
                    -plane.normalOffset[3],
                ];
                plane.flipped = !plane.flipped;
                plane.baseW = -plane.baseW;
            }
        },
        setCamera: (state, { payload }: PayloadAction<CameraState>) => {
            const goTo =
                "goTo" in payload && payload.goTo
                    ? {
                          ...payload.goTo,
                          position: Array.from(payload.goTo.position) as vec3,
                          rotation: Array.from(payload.goTo.rotation) as quat,
                      }
                    : undefined;

            const zoomTo =
                "zoomTo" in payload && payload.zoomTo
                    ? {
                          center: Array.from(payload.zoomTo.center) as vec3,
                          radius: payload.zoomTo.radius,
                      }
                    : undefined;

            state.camera = {
                ...payload,
                ...(goTo ? { goTo } : {}),
                ...(zoomTo ? { zoomTo } : {}),
            };
        },
        setInitialCamera: (state, action: PayloadAction<CadCamera>) => {
            state.initialCamera = action.payload;
        },
        setRecordCameraMoveUndoRedo: (state, action: PayloadAction<boolean>) => {
            state.recordCameraMoveUndoRedo = action.payload;
        },
        setGrid: (state, action: PayloadAction<RecursivePartial<State["grid"]>>) => {
            state.grid = mergeRecursive(state.grid, action.payload);
        },
        setPicker: (state, action: PayloadAction<State["picker"]>) => {
            state.picker = action.payload;
            if (action.payload !== Picker.SectionPlane) {
                state.section.planeHoverObjectId = null;
            }
        },
        stopPicker: (state, action: PayloadAction<State["picker"]>) => {
            if (action.payload === state.picker) {
                state.picker = Picker.Object;
                if (action.payload === Picker.SectionPlane) {
                    state.section.planeHoverObjectId = null;
                }
            }
        },
        setSectionPlaneHoverObjectId: (state, action: PayloadAction<number | null>) => {
            state.section.planeHoverObjectId = action.payload;
        },
        setViewMode: (state, action: PayloadAction<State["viewMode"]>) => {
            state.viewMode = action.payload;
        },
        setSubtreesPanelOpen: (state, action: PayloadAction<boolean>) => {
            state.isSubtreesPanelOpen = action.payload;
        },
        addLoadingHandle: (state, action: PayloadAction<State["loadingHandles"][number]>) => {
            state.loadingHandles.push(action.payload);
        },
        removeLoadingHandle: (state, action: PayloadAction<State["loadingHandles"][number]>) => {
            state.loadingHandles = state.loadingHandles.filter((handle) => handle !== action.payload);
        },
        setStamp: (state, action: PayloadAction<State["stamp"]>) => {
            state.stamp = action.payload;
        },
        setPoints: (state, action: PayloadAction<RecursivePartial<State["points"]>>) => {
            state.points = mergeRecursive(state.points, action.payload);
            state.points.deviation.colorGradient.knots.sort((a, b) => a.position - b.position);
            state.points.classificationColorGradient.knots.sort((a, b) => a.position - b.position);
        },
        setBackground: (state, action: PayloadAction<Partial<State["background"]>>) => {
            state.background = { ...state.background, ...action.payload };
        },
        setTerrain: (state, action: PayloadAction<Partial<State["terrain"]>>) => {
            state.terrain = { ...state.terrain, ...action.payload };
        },
        setSecondaryHighlight: (state, action: PayloadAction<Partial<State["secondaryHighlight"]>>) => {
            state.secondaryHighlight = { ...state.secondaryHighlight, ...action.payload };
        },
        setBreakingPointAngleThreshold: (state, action: PayloadAction<number>) => {
            state.advanced.outlines.breakingPointAngleThreshold = action.payload;
        },
        setSceneStatus: (state, action: PayloadAction<State["sceneStatus"]>) => {
            state.sceneStatus = action.payload;
        },
        setCameraDefaults: (state, action: PayloadAction<RecursivePartial<State["cameraDefaults"]>>) => {
            state.cameraDefaults = mergeRecursive(state.cameraDefaults, action.payload);
        },
        setCameraDefaultFlyTime: (state, action: PayloadAction<number>) => {
            state.cameraDefaultFlyTime = action.payload;
        },
        setAdvanced: (state, action: PayloadAction<RecursivePartial<State["advanced"]>>) => {
            state.advanced = mergeRecursive(state.advanced, action.payload);
        },
        setDeviceProfile: (state, action: PayloadAction<RecursivePartial<State["deviceProfile"]>>) => {
            state.deviceProfile = mergeRecursive(state.deviceProfile, action.payload);
        },
        setProject: (state, action: PayloadAction<RecursivePartial<State["project"]>>) => {
            state.project = mergeRecursive(state.project, action.payload);
        },
        setNavigationCube: (state, action: PayloadAction<RecursivePartial<State["navigationCube"]>>) => {
            state.navigationCube = mergeRecursive(state.navigationCube, action.payload);
        },
        setDebugStats: (state, action: PayloadAction<RecursivePartial<State["debugStats"]>>) => {
            state.debugStats = mergeRecursive(state.debugStats, action.payload);
        },
        setClippingInEdit: (state, action: PayloadAction<RecursivePartial<State["clippingInEdit"]>>) => {
            state.clippingInEdit = action.payload;
        },
        setGeneratedParametricData: (
            state,
            action: PayloadAction<RecursivePartial<State["generatedParametricData"]>>,
        ) => {
            state.generatedParametricData = mergeRecursive(state.generatedParametricData, action.payload);
        },
        setXrayOpacity: (state, action: PayloadAction<number>) => {
            state.xrayConfig.color[3] = action.payload;
        },
        setCanvasSelectionEnabled: (state, action: PayloadAction<boolean>) => {
            state.canvasSelectionEnabled = action.payload;
        },
        setObjectColor: (
            state,
            action: PayloadAction<{
                internalPaletteParams: InternalPaletteParams[];
            }>,
        ) => {
            const {
                object2normalColor,
                normalColor2objects,
                object2xrayColor,
                xrayColor2objects,
                object2selectedColor,
                selectedColor2objects,
            } = state.colorOverrides;
            const allowedKeys = ["default", "xray", "selected"];
            for (const { palette, objectIds } of action.payload.internalPaletteParams) {
                const keys = Object.keys(palette);
                const invalidKeys = keys.filter((key) => !allowedKeys.includes(key));
                if (invalidKeys.length > 0) {
                    throw new Error(
                        `Unsupported palette keys: ${invalidKeys.join(", ")}. Only 'default', 'xray', and 'selected' are allowed.`,
                    );
                }

                for (const objectId of objectIds) {
                    const normalColor = palette.default;
                    setObjectPalette(objectId, normalColor, object2normalColor, normalColor2objects);
                    const xrayColor = palette.xray;
                    setObjectPalette(objectId, xrayColor, object2xrayColor, xrayColor2objects);
                    const selectedColor = palette.selected;
                    setObjectPalette(objectId, selectedColor, object2selectedColor, selectedColor2objects);
                }
            }
        },
        clearObjectColor: (state, action: PayloadAction<{ objectIds: number[] }>) => {
            const { objectIds } = action.payload;
            const {
                object2normalColor,
                normalColor2objects,
                object2xrayColor,
                xrayColor2objects,
                object2selectedColor,
                selectedColor2objects,
            } = state.colorOverrides;

            for (const objectId of objectIds) {
                clearObjectModalColor(objectId, object2normalColor, normalColor2objects);
                clearObjectModalColor(objectId, object2xrayColor, xrayColor2objects);
                clearObjectModalColor(objectId, object2selectedColor, selectedColor2objects);
            }
        },
        clearAllObjectColor: (state) => {
            state.colorOverrides = {
                object2normalColor: {},
                normalColor2objects: {},
                object2xrayColor: {},
                xrayColor2objects: {},
                object2selectedColor: {},
                selectedColor2objects: {},
            };
        },
    },
    extraReducers: (builder) => {
        builder.addCase(initScene, (state, action) => {
            const {
                sceneData: { organization, customProperties: props, settings, tmZone, ...sceneData },
                sceneConfig,
                initialCamera,
                deviceProfile,
            } = action.payload;

            // Init camera
            state.camera.type = initialCamera.kind === "orthographic" ? CameraType.Orthographic : CameraType.Pinhole;
            state.camera.goTo = initialCamera;

            // Use 0 flyTime in tests to speed them up
            if (import.meta.env.MODE === "test" && state.camera.goTo.flyTime === undefined) {
                state.camera.goTo = { ...state.camera.goTo, flyTime: 0 };
            }

            state.savedCameraPositions.positions[0] = { ...initialCamera, kind: state.camera.type };
            state.savedCameraPositions.currentIndex = 0;

            state.sceneOrganization = organization ?? "";
            state.project.tmZone = tmZone ?? state.project.tmZone;
            state.deviceProfile = mergeRecursive(state.deviceProfile, deviceProfile);
            state.debugStats.enabled = window.location.search.includes("debug=true");

            // camera
            if (props.explorerProjectState?.camera) {
                const camera = props.explorerProjectState.camera;

                state.cameraDefaults.pinhole = camera.pinhole;
                state.cameraDefaults.pinhole.usePointerLock ??= true;
                state.cameraDefaults.orthographic = mergeRecursive(
                    state.cameraDefaults.orthographic,
                    camera.orthographic,
                );
            } else {
                state.cameraDefaults.pinhole.clipping.far = Math.max(
                    (sceneData.camera as { far?: number })?.far ?? 0,
                    DEFAULT_CAMERA_PINHOLE_FAR,
                );
                state.cameraDefaults.pinhole.clipping.near = Math.max(
                    (sceneData.camera as { near?: number })?.near ?? 0,
                    0.1,
                );
                state.cameraDefaults.orthographic.topDownElevation = props.defaultTopDownElevation;
                state.cameraDefaults.orthographic.usePointerLock =
                    props.pointerLock !== undefined
                        ? props.pointerLock.ortho
                        : state.cameraDefaults.orthographic.usePointerLock;
                state.cameraDefaults.pinhole.speedLevels = props.cameraSpeedLevels?.flight
                    ? {
                          slow: props.cameraSpeedLevels.flight.slow * 33,
                          default: props.cameraSpeedLevels.flight.default * 33,
                          fast: props.cameraSpeedLevels.flight.fast * 33,
                      }
                    : state.cameraDefaults.pinhole.speedLevels;
            }

            // corner features
            state.debugStats.enabled = props.explorerProjectState?.features?.debugStats
                ? props.explorerProjectState.features.debugStats.enabled || state.debugStats.enabled
                : Boolean(props.showStats) || state.debugStats.enabled;
            state.navigationCube.enabled =
                !state.debugStats.enabled &&
                (props.explorerProjectState?.features?.navigationCube?.enabled ?? Boolean(props.navigationCube));

            // highlights
            state.secondaryHighlight.property =
                props.explorerProjectState?.highlights?.secondary.property ??
                props.highlights?.secondary.property ??
                "";

            // generated parametric data
            if (props.explorerProjectState?.features?.generatedParametricData) {
                state.generatedParametricData.enabled =
                    props.explorerProjectState.features.generatedParametricData.enabled;
            }

            // render settings
            if (props.explorerProjectState?.renderSettings) {
                const { points, background, terrain, hide, ...advanced } = props.explorerProjectState.renderSettings;
                points.size.metric = 0; //Variable that cannot be set on novoweb and have had 2 different default. Forcing to 0;

                state.background = mergeRecursive(state.background, {
                    ...background,
                    ...(!background.url
                        ? {
                              url: state.background.url || `${action.payload.assetsUrl}/env/concrete/`,
                              blur: state.background.blur,
                          }
                        : {}),
                });
                state.points = mergeRecursive(state.points, points);
                state.subtrees = getSubtrees(hide, sceneConfig.subtrees ?? ["triangles"]);
                state.terrain = terrain;
                state.terrain.canSelect ??= false;
                state.terrain.doubleSided ??= false;
                state.terrain.elevationGradient = settings
                    ? {
                          knots: settings.terrain.elevationColors.map((node) => ({
                              position: node.elevation,
                              color: node.color,
                          })),
                      }
                    : state.terrain.elevationGradient;
                state.advanced = mergeRecursive(state.advanced, advanced);
            } else if (settings) {
                // controls
                if (props.flightFingerMap) {
                    const { rotate, orbit, pan } = props.flightFingerMap;
                    state.cameraDefaults.pinhole.controller =
                        rotate === 1
                            ? "flight"
                            : orbit === 1
                              ? pan === 2
                                  ? "cadRightPan"
                                  : "cadMiddlePan"
                              : pan === 3
                                ? "special"
                                : "hover";
                }

                // background
                state.background.color = settings.background.color ?? state.background.color;
                state.background.blur =
                    (settings.background as { skyBoxBlur?: number }).skyBoxBlur ?? state.background.blur;
                if (settings.environment) {
                    state.background.url = `${action.payload.assetsUrl}/env/${settings.environment}/`;
                    console.log(
                        "[RENDERSLICE DEBUG] Set background.url from settings.environment:",
                        state.background.url,
                    );
                }

                // points
                state.points.size = mergeRecursive(state.points.size, settings.points.size);
                state.points.deviation.index = (settings.points.deviation as { index?: number })?.index ?? 0;
                state.points.deviation.mixFactor =
                    settings.points.deviation?.mode === "mix" ? 0.5 : settings.points.deviation?.mode === "on" ? 1 : 0;
                state.points.deviation.colorGradient = {
                    knots:
                        settings.points.deviation?.colors.map((deviation) => ({
                            color: deviation.color,
                            position: deviation.deviation,
                        })) ?? [],
                };

                // subtrees
                state.subtrees = getLegacySubtrees(settings.advanced, sceneConfig.subtrees ?? ["triangles"]);

                // terrain
                state.terrain.asBackground = settings.terrain.asBackground;
                state.terrain.elevationGradient = {
                    knots: settings.terrain.elevationColors.map((node) => ({
                        position: node.elevation,
                        color: node.color,
                    })),
                };
            } else {
                state.subtrees = getSubtrees(
                    { terrain: false, triangles: false, points: false, documents: false, lines: false },
                    sceneConfig.subtrees ?? ["triangles"],
                );
            }

            // Discard full backround URL and use assetsUrl as the base
            const bgAssetName = state.background.url.match(/\/(\w+)\/?$/)?.[1];
            if (bgAssetName) {
                state.background.url = `${action.payload.assetsUrl}/env/${bgAssetName}/`;
            } else if (!state.background.url) {
                // No background URL set (e.g., Procore backend with no settings) - use default
                state.background.url = `${action.payload.assetsUrl}/env/concrete/`;
            }
        });
        builder.addCase(resetView, (state, action) => {
            const {
                initialCamera,
                sceneData: { settings, customProperties: props },
            } = action.payload;

            state.defaultVisibility = initialState.defaultVisibility;
            state.preIsolationVisibility = initialState.preIsolationVisibility;
            state.useFilterForTransparency = false;
            state.mainObject = initialState.mainObject;
            state.picker = initialState.picker;
            state.clipping = initialState.clipping;
            state.selectionBasketMode = initialState.selectionBasketMode;
            if (state.viewMode === ViewMode.Deviations) {
                state.viewMode = ViewMode.Default;
            }
            state.selectionBasketColor = initialState.selectionBasketColor;
            state.grid = initialState.grid;

            // Camera
            if (initialCamera) {
                state.camera.type =
                    initialCamera.kind === "orthographic" ? CameraType.Orthographic : CameraType.Pinhole;
                state.camera.goTo = initialCamera;
                state.savedCameraPositions = {
                    positions: [{ ...initialCamera, kind: state.camera.type }],
                    currentIndex: 0,
                };
            }

            // Point visualization
            state.points.defaultPointVisualization = initialState.points.defaultPointVisualization;
            state.points.classificationColorGradient = initialState.points.classificationColorGradient;
            state.terrain.elevationGradient = initialState.terrain.elevationGradient;

            const availableSubtrees = Object.keys(state.subtrees).filter(
                (key) => state.subtrees[key as keyof State["subtrees"]] !== SubtreeStatus.Unavailable,
            );

            if (props.explorerProjectState?.renderSettings) {
                const { points, background, terrain, hide } = props.explorerProjectState.renderSettings;

                // background
                state.background.color = background.color;

                // deviations
                state.points.deviation.index = points.deviation.index;
                state.points.deviation.mixFactor = points.deviation.mixFactor;

                // subtrees
                state.subtrees = getSubtrees(hide, availableSubtrees);

                // terrain
                state.terrain.asBackground = terrain.asBackground;
                state.terrain.canSelect = terrain.canSelect;
                state.terrain.elevationGradient = settings
                    ? {
                          knots: settings.terrain.elevationColors.map((node) => ({
                              position: node.elevation,
                              color: node.color,
                          })),
                      }
                    : (terrain.elevationGradient ?? state.terrain.elevationGradient);

                // Point visualization
                if (points.defaultPointVisualization) {
                    state.points.defaultPointVisualization = points.defaultPointVisualization;
                }
                if (points.classificationColorGradient) {
                    state.points.classificationColorGradient = {
                        ...points.classificationColorGradient,
                        highlightedPosition: undefined,
                    };
                }
            } else if (settings) {
                // background
                state.background.color = settings.background.color ?? state.background.color;

                // deviations
                state.points.deviation.index = (settings.points.deviation as { index?: number })?.index ?? 0;
                state.points.deviation.mixFactor =
                    settings.points.deviation?.mode === "mix" ? 0.5 : settings.points.deviation?.mode === "on" ? 1 : 0;

                // subtrees
                state.subtrees = getLegacySubtrees(settings.advanced, availableSubtrees);

                // terrain
                state.terrain.asBackground = settings.terrain.asBackground;
                state.terrain.elevationGradient = {
                    knots: settings.terrain.elevationColors.map((node) => ({
                        position: node.elevation,
                        color: node.color,
                    })),
                };
            }
        });
        builder.addCase(selectBookmark, (state, action) => {
            const {
                camera,
                subtrees,
                viewMode,
                objects,
                background,
                terrain,
                deviations,
                grid,
                clipping,
                options,
                pointVisualization,
                autofreezeEnabled,
            } = action.payload;

            state.camera =
                camera.kind === "orthographic"
                    ? {
                          type: CameraType.Orthographic,
                          goTo: {
                              position: [...camera.position],
                              rotation: [...camera.rotation],
                              fov: camera.fov,
                              far: camera.far ?? state.cameraDefaults[camera.kind].clipping.far,
                          },
                      }
                    : {
                          type: CameraType.Pinhole,
                          goTo: {
                              position: [...camera.position],
                              rotation: [...camera.rotation],
                          },
                      };

            state.subtrees = subtreesFromBookmark(state.subtrees, subtrees);
            state.viewMode = viewMode;
            state.selectionBasketMode =
                options.addToSelectionBasket || objects.selectionBasket.ids.length
                    ? objects.selectionBasket.mode
                    : SelectionBasketMode.Loose;
            if (objects.selectionBasket.color) {
                state.selectionBasketColor = objects.selectionBasket.color;
            }
            state.mainObject = objects.mainObject.id;
            state.defaultVisibility = options.addToSelectionBasket
                ? ((options.visibilityWhenAddingToSelectionBasket as ObjectVisibility) ?? ObjectVisibility.Transparent)
                : (objects.defaultVisibility as ObjectVisibility);
            // When a bookmark was saved in an isolated state, allow the user to unisolate.
            // We don't know the pre-isolation state from the bookmark, so default to Neutral.
            state.preIsolationVisibility =
                state.defaultVisibility === ObjectVisibility.Transparent ||
                state.defaultVisibility === ObjectVisibility.Xray
                    ? ObjectVisibility.Neutral
                    : undefined;
            if (state.defaultVisibility === ObjectVisibility.Transparent && autofreezeEnabled) {
                state.useFilterForTransparency = true;
            }
            state.background.color = background.color;
            state.terrain.asBackground = terrain.asBackground;
            state.terrain.doubleSided = terrain.doubleSided ?? false;
            state.terrain.canSelect = terrain.canSelect ?? false;
            if (terrain.elevationGradient) {
                state.terrain.elevationGradient = terrain.elevationGradient;
            }
            if (deviations) {
                state.points.deviation.index = deviations.index;
                state.points.deviation.mixFactor = deviations.mixFactor;
            }
            state.grid = grid;
            state.clipping = {
                ...clipping,
                draw: false,
                outlines: clipping.outlines !== undefined ? clipping.outlines : true,
                planes: clipping.planes.map(({ normalOffset, color, outline, anchorPos, showPlane }, idx) => ({
                    normalOffset,
                    color: color ?? clippingPlaneColors[idx],
                    showPlane: showPlane ?? false,
                    outline: {
                        enabled: outline ? outline.enabled : idx === 0,
                        color: clippingPlaneOutlineColors[idx],
                    },
                    baseW: normalOffset[3],
                    anchorPos,
                })),
            };
            if (pointVisualization) {
                state.points.defaultPointVisualization = pointVisualization.defaultPointVisualization;
                state.points.classificationColorGradient = {
                    ...pointVisualization.classificationColorGradient,
                    highlightedPosition: undefined,
                };
            }
        });
        builder.addCase(setViewpoint, (state, action) => {
            const { camera, clipping, render_mode, visibility } = action.payload;
            const ignoreUndefined = action.meta.ignoreUndefined;

            // Camera
            if (camera) {
                const cadCamera = viewpointCameraToCadCamera(camera);
                state.camera = {
                    type: cadCamera.kind === "orthographic" ? CameraType.Orthographic : CameraType.Pinhole,
                    goTo: {
                        position: cadCamera.position,
                        rotation: cadCamera.rotation,
                        fov: cadCamera.fov,
                        flyTime: action.payload.cameraFlyTime,
                    },
                };
            }

            // Default visibility
            const hasVisibilityInput = render_mode !== undefined || visibility !== undefined;
            if (hasVisibilityInput || !ignoreUndefined) {
                if (render_mode === "xray") {
                    state.defaultVisibility = ObjectVisibility.Xray;
                    state.advanced = mergeRecursive(state.advanced, {
                        pick: {
                            opacityThreshold: state.advanced.pick.xrayPickingEnabled
                                ? NORMAL_VIZ_MODE_OPACITY_THRESHOLD
                                : state.xrayConfig.color[3] + XRAY_OPACITY_THRESHOLD_OFFSET,
                        },
                    });
                } else if (visibility && !visibility.default_visibility) {
                    state.defaultVisibility = ObjectVisibility.Transparent;
                } else {
                    state.defaultVisibility = ObjectVisibility.Neutral;
                }
                // When a viewpoint was saved in an isolated state, allow the user to unisolate.
                state.preIsolationVisibility =
                    state.defaultVisibility === ObjectVisibility.Transparent ||
                    state.defaultVisibility === ObjectVisibility.Xray
                        ? ObjectVisibility.Neutral
                        : undefined;
            }

            // Clipping
            if (clipping) {
                state.clipping = {
                    enabled: clipping.enabled,
                    mode: clipping.mode === "intersection" ? ClippingMode.intersection : ClippingMode.union,
                    draw: clipping.draw_planes,
                    outlines: clipping.draw_outlines,
                    planes: (clipping.planes ?? []).map((plane, idx) => {
                        const dir = plane.direction;
                        const w = vec3.dot(plane.location as vec3, dir as vec3);
                        const normalOffset = vec4.fromValues(dir[0], dir[1], dir[2], w);
                        return {
                            normalOffset,
                            color: clippingPlaneColors[idx],
                            showPlane: false,
                            anchorPos: plane.location,
                            outline: {
                                enabled: idx === 0,
                                color: clippingPlaneOutlineColors[idx],
                            },
                            baseW: w,
                        };
                    }),
                };
            } else if (!ignoreUndefined) {
                state.clipping = {
                    enabled: false,
                    mode: ClippingMode.union,
                    draw: false,
                    outlines: true,
                    planes: [],
                };
            }
        });
    },
});

function subtreesFromBookmark(
    current: State["subtrees"],
    bm: NonNullable<Bookmark["explorerState"]>["subtrees"],
): State["subtrees"] {
    const subtrees = { ...current };

    subtrees.lines =
        subtrees.lines !== SubtreeStatus.Unavailable
            ? bm.lines
                ? SubtreeStatus.Shown
                : SubtreeStatus.Hidden
            : subtrees.lines;

    subtrees.points =
        subtrees.points !== SubtreeStatus.Unavailable
            ? bm.points
                ? SubtreeStatus.Shown
                : SubtreeStatus.Hidden
            : subtrees.points;

    subtrees.terrain =
        subtrees.terrain !== SubtreeStatus.Unavailable
            ? bm.terrain
                ? SubtreeStatus.Shown
                : SubtreeStatus.Hidden
            : subtrees.terrain;

    subtrees.triangles =
        subtrees.triangles !== SubtreeStatus.Unavailable
            ? bm.triangles
                ? SubtreeStatus.Shown
                : SubtreeStatus.Hidden
            : subtrees.triangles;

    subtrees.documents =
        subtrees.documents !== SubtreeStatus.Unavailable
            ? bm.documents
                ? SubtreeStatus.Shown
                : SubtreeStatus.Hidden
            : subtrees.documents;

    return subtrees;
}

function setObjectPalette(
    objectId: number,
    modalColor: Color | undefined,
    object2modalColor: Record<number, number[]>,
    modalColor2objects: Record<string, number[]>,
): void {
    // If there is no modalColor, we don't need to do anything
    if (!modalColor) {
        return;
    }

    // Convert color to hex string with opacity
    const newColor = fullFormHexToVecRGBA(modalColor.color, modalColor.opacity);

    // If the old color is the same as the new color, we don't need to do anything
    const oldColor = object2modalColor[objectId];
    if (oldColor === newColor) {
        // No changes needed, we're done here
        return;
    }

    // Update object-2-modalColor mapping
    object2modalColor[objectId] = newColor;

    // Remove objectId from oldColor’s array (if there was an oldColor)
    if (oldColor) {
        const oldColorIndex = oldColor.join("|");
        const arr = modalColor2objects[oldColorIndex] || [];
        const filtered = arr.filter((id) => id !== objectId);
        if (filtered.length === 0) {
            // delete the oldColor key if its bucket is now empty
            delete modalColor2objects[oldColorIndex];
        } else {
            modalColor2objects[oldColorIndex] = filtered;
        }
    }

    // Add objectId into the new color’s bucket
    const newColorIndex = newColor.join("|");
    const prevArr = modalColor2objects[newColorIndex] ?? [];
    if (!prevArr.includes(objectId)) {
        modalColor2objects[newColorIndex] = [...prevArr, objectId];
    }
}

function clearObjectModalColor(
    objectId: number,
    object2modalColor: Record<number, number[]>,
    modalColor2objects: Record<string, number[]>,
): void {
    // If there is no color override, we don't need to do anything
    const oldColor = object2modalColor[objectId];
    if (!oldColor) {
        return;
    }

    // Remove objectId from object-2-modal color mapping
    delete object2modalColor[objectId];

    // Remove objectId from oldColor’s bucket
    const oldColorIndex = oldColor.join("|");
    const arr = modalColor2objects[oldColorIndex] || [];
    const filtered = arr.filter((id: number) => id !== objectId);
    if (filtered.length === 0) {
        delete modalColor2objects[oldColorIndex];
    } else {
        modalColor2objects[oldColorIndex] = filtered;
    }
}

const clippingPlaneColors: vec4[] = [
    [255, 255, 255],
    [37, 55, 70],
    [214, 30, 92],
    [97, 94, 155],
    [225, 224, 0],
    [255, 88, 93],
].map((v) => [...vec3.scale(v as vec3, v as vec3, 1 / 255), 0.5]);

const clippingPlaneOutlineColors: VecRGB[] = [
    [0, 0, 0],
    [255, 88, 93],
    [225, 224, 0],
    [118, 134, 146],
    [214, 30, 92],
    [37, 55, 70],
].map((v) => vec3.scale(v as vec3, v as vec3, 1 / 255));

export const selectMainObject = (state: RootState) => state.render.mainObject;
export const selectMarkerDragHoverObject = (state: RootState) => state.render.markerDragHoverObject;
export const selectDefaultVisibility = (state: RootState) => state.render.defaultVisibility;
export const selectPreIsolationVisibility = (state: RootState) => state.render.preIsolationVisibility;
export const selectIsXRayActive = (state: RootState) => state.render.defaultVisibility === ObjectVisibility.Xray;
export const selectUseFilterForTransparency = (state: RootState) => state.render.useFilterForTransparency;
export const selectSelectMultiple = (state: RootState) => state.render.selectMultiple;
export const selectSelectBox = (state: RootState) => state.render.selectBox;
export const selectCameraSpeedLevels = (state: RootState) => state.render.cameraDefaults.pinhole.speedLevels;
export const selectCurrentCameraSpeedLevel = (state: RootState) => state.render.currentCameraSpeedLevel;
export const selectSavedCameraPositions = (state: RootState) =>
    state.render.savedCameraPositions as SavedCameraPositions;
export const selectSubtrees = (state: RootState) => state.render.subtrees;
export const selectSelectionBasketMode = (state: RootState) => state.render.selectionBasketMode;
export const selectSelectionBasketColor = (state: RootState) => state.render.selectionBasketColor;
export const selectClippingPlanes = (state: RootState) => state.render.clipping;
export const selectCamera = (state: RootState) => state.render.camera as CameraState;
export const selectInitialCamera = (state: RootState) => state.render.initialCamera;
export const selectRecordCameraMoveUndoRedo = (state: RootState) => state.render.recordCameraMoveUndoRedo;
export const selectCameraType = (state: RootState) => state.render.camera.type;
export const selectSecondaryHighlightProperty = (state: RootState) => state.render.secondaryHighlight.property;
export const selectBreakingPointAngleThreshold = (state: RootState) =>
    state.render.advanced.outlines.breakingPointAngleThreshold;
export const selectProjectSettings = (state: RootState) => state.render.project;
export const selectGrid = (state: RootState) => state.render.grid;
export const selectPicker = (state: RootState) => state.render.picker;
export const selectViewMode = (state: RootState) => state.render.viewMode;
export const selectIsSubtreesPanelOpen = (state: RootState) => state.render.isSubtreesPanelOpen;
export const selectLoadingHandles = (state: RootState) => state.render.loadingHandles;
export const selectStamp = (state: RootState) => state.render.stamp;
export const selectPointerLock = (state: RootState) => state.render.cameraDefaults.orthographic.usePointerLock;
export const selectProportionalCameraSpeed = (state: RootState) =>
    state.render.cameraDefaults.pinhole.proportionalSpeed;
export const selectBackground = (state: RootState) => state.render.background;
export const selectSceneStatus = (state: RootState) => state.render.sceneStatus;
export const selectTerrain = (state: RootState) => state.render.terrain;
export const selectCameraDefaults = (state: RootState) => state.render.cameraDefaults;
export const selectCameraDefaultFlyTime = (state: RootState) => state.render.cameraDefaultFlyTime;
export const selectAdvanced = (state: RootState) => state.render.advanced;
export const selectDeviceProfile = (state: RootState) => state.render.deviceProfile;
export const selectAutofreezeEnabled = (state: RootState) => state.render.deviceProfile.autofreezeEnabled;
export const selectPoints = (state: RootState) => state.render.points;
export const selectDeviations = (state: RootState) => state.render.points.deviation;
export const selectNavigationCube = (state: RootState) => state.render.navigationCube;
export const selectDebugStats = (state: RootState) => state.render.debugStats;
export const selectClippingInEdit = (state: RootState) => state.render.clippingInEdit;
export const selectSceneOrganization = (state: RootState) => state.render.sceneOrganization;
export const selectGeneratedParametricData = (state: RootState) => state.render.generatedParametricData;
// Use "Generated parametric data" flag only for Measurement widget -
// in this widget we show actual numbers and rely on precision,
// in other widgets we just use it for snapping, so it's safe to always use generated parametric data.
export const selectCurrentPickerGeneratedParametricData = createSelector(
    [selectPicker, selectGeneratedParametricData],
    (picker, generatedParametricData) => (picker === Picker.Measurement ? generatedParametricData : { enabled: true }),
);
export const selectClassificationColorGradient = (state: RootState) => state.render.points.classificationColorGradient;
export const selectDefaultPointVisualization = (state: RootState) => state.render.points.defaultPointVisualization;
export const selectIsMobile = (state: RootState) => state.render.deviceProfile.isMobile;
export const selectXrayConfig = (state: RootState) => state.render.xrayConfig;
export const selectObject2NormalColorOverrides = (state: RootState) => state.render.colorOverrides.object2normalColor;
export const selectNormalColorOverrides2Objects = (state: RootState) => state.render.colorOverrides.normalColor2objects;
export const selectObject2XrayColorOverrides = (state: RootState) => state.render.colorOverrides.object2xrayColor;
export const selectXrayColorOverrides2Objects = (state: RootState) => state.render.colorOverrides.xrayColor2objects;
export const selectObject2SelectedColorOverrides = (state: RootState) =>
    state.render.colorOverrides.object2selectedColor;
export const selectSelectedColorOverrides2Objects = (state: RootState) =>
    state.render.colorOverrides.selectedColor2objects;
export const selectCanvasSelectionEnabled = (state: RootState) => state.render.canvasSelectionEnabled;
export const selectSectionPlaneHoverObjectId = (state: RootState) => state.render.section.planeHoverObjectId;

const { reducer } = renderSlice;
const actions = { ...renderSlice.actions, initScene, resetView, selectBookmark, setViewpoint };
export { actions as renderActions, reducer as renderReducer };
export type { State as RenderState };
